import { stampBounds, stampMatrix } from '../brush/geometry';
import { PathSmoother, SpacingWalker } from '../brush/path';
import { innerRadiusFor, jitterStamp, makeRandom } from '../brush/tip';
import type { BrushDynamics, BrushTip } from '../brush/types';
import { clamp, rectAlignToPixels, rectUnion } from '../core/math';
import type { DocumentMeta, Point, Rect } from '../core/types';
import type { RenderTarget } from '../gl/renderTarget';
import type { Texture } from '../gl/texture';
import type { Engine } from './engine';
import type { EngineHistoryOp } from './historyOps';
import type { EffectContext, StrokeApplier } from './paintEffects';
import type { DocumentResources } from './resources';
import type { StrokeTarget } from './strokeTarget';

export interface StrokeParams {
  /** Tip diameter in document pixels. */
  size: number;
  tip: BrushTip;
  dynamics: BrushDynamics;
  /** 0..1 coverage of one stamp. */
  flow: number;
  /** 0 = raw pointer positions, 0.9 = maximum lag. */
  smoothing: number;
  /** Pressure is only honoured for a stylus; a mouse reports a constant value. */
  usePressure: boolean;
  /** Pencil: binary edges instead of antialiased ones. */
  sharp: boolean;
  /** Builds what the coverage does to the pixels underneath it. */
  applier: (ctx: EffectContext) => StrokeApplier;
}

const EMPTY_RECT: Rect = { x: 0, y: 0, width: 0, height: 0 };

/**
 * One brush stroke from pointer-down to pointer-up.
 *
 * Stamps accumulate into a single-channel coverage texture, and the destination
 * itself is only touched once, on commit, which is what makes a stroke a single
 * undo step. The destination may be a layer surface, a mask or a channel — see
 * `StrokeTarget` — and what the coverage means is decided by the applier.
 */
export class StrokeSession {
  private readonly resources: DocumentResources;
  private readonly coverage: RenderTarget;
  private readonly walker = new SpacingWalker();
  private readonly smoother: PathSmoother;
  private readonly random = makeRandom((Math.random() * 0xffffffff) >>> 0);
  private readonly tipTexture: Texture | null;
  private readonly applier: StrokeApplier;
  private previousDab: Point | null = null;
  private dirty: Rect = EMPTY_RECT;
  private stamped = false;
  private committed = false;

  constructor(
    private readonly engine: Engine,
    private readonly doc: DocumentMeta,
    private readonly target: StrokeTarget,
    private readonly params: StrokeParams,
    private readonly contentOffset: Point = { x: 0, y: 0 },
  ) {
    this.resources = engine.resourcesFor(doc);
    this.coverage = this.resources.r8(0);
    this.smoother = new PathSmoother(params.smoothing);
    this.tipTexture = engine.tipTexture(params.tip.kind === 'pattern' ? params.tip.imageId : null);
    this.coverage.clear(0, 0, 0, 0);
    this.applier = params.applier({
      renderer: engine.renderer,
      resources: this.resources,
      width: doc.width,
      height: doc.height,
      target,
      selection: this.resources.selection.activeTexture,
      contentOffset: this.contentOffset,
    });
    this.applier.begin?.();
    this.refreshPreview();
  }

  /** Texture to composite in place of the destination while the stroke is live. */
  get previewTexture(): Texture {
    return this.target.preview.texture;
  }

  get hasContent(): boolean {
    return this.stamped;
  }

  begin(point: Point, pressure: number, emitFirst = true): void {
    const start = this.smoother.reset(point);
    this.walker.begin(
      start,
      this.stepFor(pressure),
      (at, direction) => this.stamp(at, direction, pressure),
      emitFirst,
    );
    this.refreshPreview();
  }

  extend(point: Point, pressure: number): void {
    const smoothed = this.smoother.push(point);
    this.walker.extend(smoothed, this.stepFor(pressure), (at, direction) =>
      this.stamp(at, direction, pressure),
    );
    this.refreshPreview();
  }

  /** Writes the stroke into the destination and returns the ops that undo it. */
  commit(): EngineHistoryOp[] | null {
    if (this.committed) return null;
    this.committed = true;
    if (!this.stamped) return null;

    const localDirty = {
      x: this.dirty.x - this.contentOffset.x,
      y: this.dirty.y - this.contentOffset.y,
      width: this.dirty.width,
      height: this.dirty.height,
    };
    const rect = rectAlignToPixels(localDirty, this.doc.width, this.doc.height);
    if (rect.width <= 0 || rect.height <= 0) return null;

    const before = this.target.readPixels(rect);
    if (this.applier.finalise) this.applier.finalise(this.coverage.texture, this.dirty);
    else this.refreshPreview();
    this.engine.renderer.copy(this.target.framebuffer, this.target.preview.texture);
    const after = this.target.readPixels(rect);

    return [this.target.historyOp(rect, before, after)];
  }

  cancel(): void {
    this.committed = true;
  }

  private pressureScale(pressure: number): number {
    return this.params.usePressure ? clamp(pressure, 0.05, 1) : 1;
  }

  private sizeFor(pressure: number): number {
    const scale = this.params.dynamics.pressureSize ? this.pressureScale(pressure) : 1;
    return Math.max(1, this.params.size * scale);
  }

  private stepFor(pressure: number): number {
    return Math.max(0.5, this.sizeFor(pressure) * Math.max(0.01, this.params.tip.spacing));
  }

  private stamp(point: Point, direction: number, pressure: number): void {
    const { tip, dynamics } = this.params;
    const flowScale = dynamics.pressureOpacity ? this.pressureScale(pressure) : 1;
    const placement = jitterStamp(
      {
        x: point.x,
        y: point.y,
        size: this.sizeFor(pressure),
        angle: tip.angle,
        roundness: tip.roundness,
        flow: this.params.flow * flowScale,
      },
      dynamics,
      direction,
      this.random,
    );

    const width = placement.size;
    const height = placement.size * placement.roundness;
    const inner = innerRadiusFor(tip.hardness, width / 2);
    const matrix = stampMatrix(
      { x: placement.x, y: placement.y },
      width,
      height,
      placement.angle,
      this.doc.width,
      this.doc.height,
    );

    this.engine.renderer.drawStamp(this.coverage.framebuffer, matrix, {
      inner,
      flow: placement.flow,
      tip: this.tipTexture,
      sharp: this.params.sharp,
    });

    this.applier.dab?.({
      x: placement.x,
      y: placement.y,
      width,
      height,
      angle: placement.angle,
      flow: placement.flow,
      previous: this.previousDab,
      tip: this.tipTexture,
      inner,
      sharp: this.params.sharp,
    });
    this.previousDab = { x: placement.x, y: placement.y };

    this.dirty = rectUnion(
      this.dirty,
      stampBounds({ x: placement.x, y: placement.y }, width, height, placement.angle),
    );
    this.stamped = true;
  }

  private refreshPreview(): void {
    this.applier.preview(this.coverage.texture);
  }
}
